"use client"
import type { Currency } from "@opensea/app-kit"
import { getCurrencyKey, useChains } from "@opensea/app-kit"
import {
  FlexColumn,
  Input,
  Item,
  ItemAction,
  ItemAvatar,
  ItemContent,
  ItemDescription,
  ItemTitle,
  NumberDisplay,
  SpaceBetween,
  TextBody,
  Tooltip,
  UnstyledButton,
} from "@opensea/ui-kit"
import { ChevronRight, SwapVert } from "@opensea/ui-kit/icons"
import { useCallback, useState } from "react"
import { useFormContext, useFormState, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { Avatar } from "../../../../components/Avatar"
import { CurrencyQuantityPresetButton } from "../../../../components/CurrencyQuantityInput/CurrencyQuantityPresetButton"
import { CurrencySelectSheet } from "../../../../components/CurrencyQuantityInput/CurrencySelectSheet"
import { useSyncFormFields } from "../../../../hooks/useSyncFormField"
import { useBalances } from "../../../../providers/BalancesProvider"
import { convertTokenToUsd, convertUsdToToken } from "../helpers"
import { useSendQuote } from "../provider/SendProvider"
import type { SendFlowFormValues } from "../schema"

type TokenQuantityInputProps = {
  container?: HTMLElement | null
}

type CurrencyMode = "token" | "usd"

export function TokenQuantityInput({ container }: TokenQuantityInputProps) {
  const t = useTranslations("wallet.TokenQuantityInput")
  const [isSelectingCurrency, setIsSelectingCurrency] = useState(false)
  const { getBalanceOfCurrency } = useBalances()
  const { currency, setCurrency } = useSendQuote()
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>("token")
  const { getName: getChainName, getLogo: getChainLogo } = useChains()

  const { setValue: setFormValue, control } =
    useFormContext<SendFlowFormValues>()

  const { errors } = useFormState({ control })

  const balance = getBalanceOfCurrency(currency)
  useSyncFormFields({
    fields: [{ name: "ownedQuantity", value: Number(balance?.quantity) }],
    setValue: setFormValue,
  })

  const quantity = useWatch({ control, name: "quantity" })

  // This is what is displayed in the input, since it can be in USD or token
  const [displayValue, setDisplayValue] = useState(quantity)
  // This is the value that is displayed below the input representing the converted value
  const alternateValue =
    currencyMode === "token" ? convertTokenToUsd(quantity, currency) : quantity

  const handleCurrencyChange = useCallback(
    (currency: Currency) => {
      setCurrency(currency)
      setFormValue("quantity", "0")
      setDisplayValue("0")
      setIsSelectingCurrency(false)
    },
    [setCurrency, setDisplayValue, setFormValue],
  )

  const handleQuantityChange = useCallback(
    (val: string) => {
      setDisplayValue(val)
      const fieldValue =
        currencyMode === "usd" ? convertUsdToToken(val, currency) : val
      setFormValue("quantity", fieldValue, {
        shouldValidate: true,
      })
    },
    [currency, currencyMode, setDisplayValue, setFormValue],
  )

  const handleSwitchCurrencyMode = useCallback(() => {
    setDisplayValue(alternateValue)
    setCurrencyMode(prevMode => (prevMode === "token" ? "usd" : "token"))
  }, [alternateValue])

  return (
    <>
      <FlexColumn className="gap-3">
        <Item
          className="rounded-md border-none bg-bg-additional-1 px-4 py-3.5 hover:cursor-pointer hover:bg-bg-additional-2"
          onClick={() => setIsSelectingCurrency(true)}
        >
          <ItemAvatar
            badge={
              currency.chainIdentifier ? (
                <Tooltip
                  content={t("chain", {
                    name: getChainName(currency.chainIdentifier),
                  })}
                >
                  <Avatar.Badge className="bg-black" size="md">
                    <Avatar
                      className="text-white"
                      title={getChainName(currency.chainIdentifier)}
                      {...getChainLogo(currency.chainIdentifier)}
                    />
                  </Avatar.Badge>
                </Tooltip>
              ) : undefined
            }
            className="rounded-full bg-white"
            seed={getCurrencyKey(currency)}
            size={40}
            src={currency.imageUrl}
          />
          <ItemContent>
            <ItemTitle>{currency.symbol}</ItemTitle>
            <ItemDescription>
              <NumberDisplay
                suffix={currency.symbol}
                value={balance?.quantity ?? 0}
              />
            </ItemDescription>
          </ItemContent>

          <ItemAction>
            <UnstyledButton>
              <ChevronRight className="rotate-90" fill="text-secondary" />
            </UnstyledButton>
          </ItemAction>
        </Item>

        <SpaceBetween className="items-center">
          <Input
            className="!bg-bg-additional-1 py-7 font-mono"
            endEnhancer={
              <CurrencyQuantityPresetButton
                balance={balance}
                balanceMultiplier={1}
                onClick={presetQuantity => {
                  const display =
                    currencyMode === "usd"
                      ? convertTokenToUsd(String(presetQuantity), currency)
                      : String(presetQuantity)
                  handleQuantityChange(display)
                }}
                quantity={
                  typeof quantity === "string" && quantity !== ""
                    ? Number(quantity)
                    : undefined
                }
                requiresSvmFee={false}
              />
            }
            error={Boolean(errors.quantity)}
            onChange={e => handleQuantityChange(e.target.value)}
            placeholder="0"
            size="md"
            startEnhancer={currencyMode === "usd" ? "$" : null}
            type="number"
            value={displayValue}
          />
        </SpaceBetween>

        <SpaceBetween>
          {errors.quantity ? (
            <TextBody color="error-1" size="xs">
              {errors.quantity.message}
            </TextBody>
          ) : (
            <TextBody className="font-mono" size="xs">
              <NumberDisplay
                display={currencyMode === "usd" ? "standard" : "usd"}
                suffix={currencyMode === "usd" ? currency.symbol : "USD"}
                value={alternateValue}
              />
            </TextBody>
          )}

          <UnstyledButton
            className="text-secondary text-sm"
            onClick={handleSwitchCurrencyMode}
          >
            <SwapVert size={16} />
            {t("switchTo", {
              currency: currencyMode === "token" ? "USD" : currency.symbol,
            })}
          </UnstyledButton>
        </SpaceBetween>
      </FlexColumn>
      <CurrencySelectSheet
        container={container}
        direction="from"
        onOpenChange={setIsSelectingCurrency}
        onSelectCurrency={handleCurrencyChange}
        open={isSelectingCurrency}
        removeSwapDisabledCurrencies={false}
      />
    </>
  )
}
